import { IntlShape } from '@redbubble/next/intl';
import { uniqBy } from 'lodash';
import { ArrayElement } from 'types/ArrayElement';
import {
  experience1,
  experience2,
  experience3,
  experience4,
  experience5,
  experience6,
  experience7,
  experience8,
  experience9,
  experience10,
  experience11,
  experience12,
} from '../data';
import {
  DataIndexes,
  MeasurementUnit,
  PreQuerySizeGuideData,
  ProductMeasurementsTableData,
  ProductTypeWithSizeGuide,
  SizeGuideAttribute,
  SizeGuideRow,
  SizeGuideTable,
  StringOrIntl,
  TableColumnValues,
  TableData,
  TableRowValues,
  TitleColumnData,
} from '../types';

const enabledUnitAppendNames = new Set([
  'chest',
  'length',
  'width',
  'height',
  'depth',
  'diameter',
  'circumference',
  'circumferenceMax',
  'circumferenceMin',
  'strap',
  'hip',
  'waist',
  'inseam',
  'backLength',
  'strapLength',
  'strapWidth',
]); // Ideally this will come from IHS in the future

const appendUnit = (
  shouldAppend: boolean,
  unit: MeasurementUnit,
  value: string
) => {
  return `${value}${shouldAppend ? unit.toLowerCase() : ''}`;
};

const isUnit =
  (unit: MeasurementUnit) =>
  ({ value }: SizeGuideAttribute) =>
    value === unit;

const filterOutFirstElement = (_: unknown, i: number): boolean => i !== 0;

const getAttributes = ({ attributes }: SizeGuideRow): SizeGuideAttribute[] =>
  attributes;

const getFirstRowColumnAttributes =
  (unit: MeasurementUnit) =>
  ({ attributes }: SizeGuideRow): SizeGuideAttribute => {
    const shouldAppend = enabledUnitAppendNames.has(attributes[0].name);

    return {
      name: attributes[0].value,
      value: appendUnit(shouldAppend, unit, attributes[0].value),
      attributes: attributes[0].attributes,
    };
  };

const getColumnDataIndex = ({ dataIndex }: TableColumnValues) => dataIndex;

const getRowTitleColumnData =
  (firstColumn: TableColumnValues) =>
  (elem: SizeGuideAttribute): TitleColumnData => ({
    [firstColumn.dataIndex]: elem,
  });

const buildColumnData = ({ name, value }): TableColumnValues => ({
  dataIndex: name,
  title: value,
  align: 'center',
});

const filterRowsByColumns = (
  row: SizeGuideAttribute[],
  columns: SizeGuideAttribute[]
) => row.filter((cell) => columns.some((column) => column.name === cell.name));

const buildRow =
  (
    firstColumn: TableColumnValues,
    dataIndexes: DataIndexes,
    rowAttributes: SizeGuideAttribute[][],
    unit: MeasurementUnit
  ) =>
  (titleColumnData: TitleColumnData, rowIndex: number) => {
    // check if we should append units on this row
    const shouldAppend = enabledUnitAppendNames.has(
      titleColumnData[firstColumn.dataIndex].name
    );

    // Create title column
    const titleColumn = {
      [firstColumn.dataIndex]: titleColumnData[firstColumn.dataIndex].value,
    };

    // build out remaining columns in row
    const remainingColumns = dataIndexes.reduce(
      (
        acc: TableRowValues,
        dataIndex: ArrayElement<DataIndexes>,
        columnIndex: number
      ): TableRowValues => {
        // spread out previous acc
        // set dataindex to property name
        // set value of index to rowIndex + 1
        // rowIndex + 1 is due to first row being in header and we want to skip
        return {
          ...acc,
          [dataIndex]: appendUnit(
            shouldAppend,
            unit,
            rowAttributes[columnIndex][rowIndex + 1].value
          ),
        };
      },
      {}
    );

    return { ...titleColumn, ...remainingColumns };
  };

export const transposeSizeGuideTable = (
  inputData: SizeGuideTable,
  unit: MeasurementUnit
): TableData => {
  const { columns: inputColumns, rows: inputRows } = inputData;

  // skip if there are not enough rows or columns
  if (inputColumns.length === 0 || inputRows.length === 0)
    return { columns: [], rows: [] };

  // get first column
  const firstColumn = {
    dataIndex: inputColumns[0].name,
    title: inputColumns[0].value,
    align: 'left',
  };

  // build rest of columns from rows
  const restOfColumns = inputRows
    .filter(isUnit(unit)) // filter out anything that is not unit
    .map(getFirstRowColumnAttributes(unit)) // get attributes from first row element
    .map(buildColumnData); // build column data from attributes

  // combine firstColumn and restOfColumns, filtering out duplicate columns
  const columns = uniqBy([firstColumn, ...restOfColumns], 'dataIndex');

  // get row attributes
  const rowAttributes = inputRows
    .filter(isUnit(unit)) // filter out anything that is not unit
    .map(getAttributes) // get attributes from looped row element
    .map((row) => filterRowsByColumns(row, inputColumns)); // filter out any rows that don't have a corresponding column

  // get column data indexes, to be reduced to generate row object
  const dataIndexes: DataIndexes = restOfColumns.map(getColumnDataIndex);

  // build rows from columns
  const rows = inputColumns
    .filter(filterOutFirstElement) // filter out first column
    .map(getRowTitleColumnData(firstColumn)) // get row title to transpose from provided column
    .map(buildRow(firstColumn, dataIndexes, rowAttributes, unit)); // build row from data

  return { columns, rows };
};

export function getProductMeasurementsTableData(
  inputData: SizeGuideTable
): ProductMeasurementsTableData {
  const inches = transposeSizeGuideTable(inputData, MeasurementUnit.In);
  const cm = transposeSizeGuideTable(inputData, MeasurementUnit.Cm);
  return { IN: inches, CM: cm };
}

export const getSizeGuideExperience = (
  productTypeId: string
): PreQuerySizeGuideData | null => {
  switch (productTypeId) {
    case ProductTypeWithSizeGuide.CactusBizzo:
      return experience1;
    case ProductTypeWithSizeGuide.SookySanga:
      return experience2;
    case ProductTypeWithSizeGuide.ClueyKookaburra:
      return experience3;
    case ProductTypeWithSizeGuide.UnrealChinwag:
      return experience4;
    case ProductTypeWithSizeGuide.FairCuppa:
    case ProductTypeWithSizeGuide.JumpingBoot:
    case ProductTypeWithSizeGuide.BonzaLamington:
      return experience5;
    case ProductTypeWithSizeGuide.WhingingMagpie:
    case ProductTypeWithSizeGuide.AggroEmu:
      return experience6;
    case ProductTypeWithSizeGuide.RipperRissole:
      return experience7;
    case ProductTypeWithSizeGuide.BillowingParachute:
      return experience8;
    case ProductTypeWithSizeGuide.BlockishWrapper:
      return experience9;
    case ProductTypeWithSizeGuide.BillowingFalsehood:
      return experience10;
    case ProductTypeWithSizeGuide.BillowingSwelter:
      return experience11;
    case ProductTypeWithSizeGuide.CalmWrapper:
      return experience12;
    default:
      return null;
  }
};

export const getTranslatedMessage = (
  message: StringOrIntl,
  intl: IntlShape
): string => {
  if (typeof message === 'string') {
    return message;
  }
  return intl.formatMessage(message);
};

export const getMeasurementUnit = (currency: string) =>
  currency === 'USD' ? MeasurementUnit.In : MeasurementUnit.Cm;
